import initSkipLink from './modules/skip-link';
import {
    runAfterLoad,
    runChunkedTasks,
    runWhenDomReady,
    runWhenIdle,
} from "./util/scheduler";

const THIRD_PARTY_TELEMETRY_DOMAINS = [
    'ct.pinterest.com',
    'googletagmanager.com',
    'px.ads.linkedin.com',
    'snap.licdn.com',
];

const REQUIRED_THIRD_PARTY_DOMAINS = [
    'js.stripe.com',
    'maps.googleapis.com',
];

const FAILED_FETCH_MESSAGES = [
    'Failed to fetch',
    'NetworkError when attempting to fetch resource',
];

function getHostname(url) {
    try {
        return new URL(url, window.location.origin).hostname;
    } catch(error) {
        return '';
    }
}

function isThirdPartyTelemetryUrl(url) {
    const hostname = getHostname(url);

    if(!hostname) {
        return false;
    }

    return THIRD_PARTY_TELEMETRY_DOMAINS.some(domain => hostname === domain || hostname.endsWith(`.${domain}`));
}

function isRequiredThirdPartyUrl(url) {
    const hostname = getHostname(url);

    if(!hostname) {
        return false;
    }

    if(hostname === window.location.hostname) {
        return true;
    }

    return REQUIRED_THIRD_PARTY_DOMAINS.some(domain => hostname === domain || hostname.endsWith(`.${domain}`));
}

function getErrorMessage(error) {
    if(typeof error === 'string') {
        return error;
    }

    if(error && typeof error.message === 'string') {
        return error.message;
    }

    return '';
}

function isFailedFetchMessage(message) {
    return FAILED_FETCH_MESSAGES.some(fragment => message.includes(fragment));
}

function getTelemetryDomainMatch(values) {
    return values.find(value => isThirdPartyTelemetryUrl(value) && !isRequiredThirdPartyUrl(value)) || '';
}

function getEventUrls(event) {
    const urls = [];

    const exceptionValues = event && event.exception && Array.isArray(event.exception.values)
        ? event.exception.values
        : [];

    exceptionValues.forEach(value => {
        if(!value || !value.stacktrace || !Array.isArray(value.stacktrace.frames)) {
            return;
        }

        value.stacktrace.frames.forEach(frame => {
            if(frame && typeof frame.filename === 'string') {
                urls.push(frame.filename);
            }
        });
    });

    return urls;
}

function getTelemetryFailureDomain(reason, event = null) {
    const message = getErrorMessage(reason);

    if(!isFailedFetchMessage(message)) {
        return '';
    }

    if(event) {
        return getTelemetryDomainMatch(getEventUrls(event));
    }

    if(reason && typeof reason === 'object' && reason.stack) {
        return getTelemetryDomainMatch([reason.stack]);
    }

    return '';
}

function isThirdPartyTelemetryEvent(event, hint) {
    const originalException = hint && hint.originalException ? hint.originalException : null;

    return Boolean(
        getTelemetryFailureDomain(originalException, event) ||
        getTelemetryFailureDomain(event && event.message ? event.message : '', event)
    );
}

function installTelemetryRejectionGuard() {
    window.addEventListener('unhandledrejection', event => {
        const telemetryDomain = getTelemetryFailureDomain(event.reason);

        if(telemetryDomain) {
            event.preventDefault();
            console.warn('Best-effort telemetry request failed', telemetryDomain);
        }
    });
}

function getFetchInitWithoutAttributionReporting(init) {
    if(!init || typeof init !== 'object' || !('attributionReporting' in init)) {
        return init;
    }

    const sanitizedInit = { ...init };
    delete sanitizedInit.attributionReporting;

    return sanitizedInit;
}

function installTelemetryFetchGuard() {
    const originalFetch = window.fetch;
    window.fetch = function(input, init) {
        const url = input instanceof Request ? input.url : String(input ?? '');
        if(isThirdPartyTelemetryUrl(url)) {
            return originalFetch.call(this, input, getFetchInitWithoutAttributionReporting(init)).catch(error => {
                console.warn('Best-effort telemetry request failed', getHostname(url), error.message || error);
            });
        }
        return originalFetch.call(this, input, init);
    };
}

function initCriticalNavigation() {
    import('./modules/nav-mobile').then(({ default: NavMobile }) => {
        window.navMobile = new NavMobile();
    });
}

function initDeferredNavigation() {
    runWhenIdle(() => {
        import('./modules/nav-desktop').then(({ default: NavDesktop }) => {
            window.navDesktop = new NavDesktop();
        });
    });

    runWhenIdle(() => {
        import('./modules/nav-footer').then(({ default: NavFooter }) => {
            window.navFooter = new NavFooter();
        });
    });

    runWhenIdle(() => {
        import('./util/smoothscroll-anchors');
    });
}

function initFormInputs() {
    if(!document.querySelector('.text-input, input[data-autocomplete-contractors]')) {
        return;
    }

    runWhenIdle(() => {
        import('./util/inputs').then(({ initializeInputs }) => initializeInputs());
    });
}

function initSentry() {
    const env = window.wp_ajax?.WP_ENV;
    const release = window.wp_ajax?.SENTRY_RELEASE;
    const hasRelease = Boolean(release) && release !== 'unknown';
    const shouldSkip = (env === 'development' || env === 'local') && !hasRelease;
    const allowedEnvironments = ['production', 'staging', 'development', 'local'];

    if (shouldSkip || !allowedEnvironments.includes(env)) {
        return;
    }

    runAfterLoad(() => {

        import('@sentry/browser').then((Sentry) => {
            Sentry.init({
                dsn: "https://73290b84ebd230f3365968a561336a13@o4506074179174400.ingest.sentry.io/4506152112816128",

                allowUrls: [
                    'malarkeyroofing.com',
                ],
                // sampleRate governs error events only. Structured logs are a
                // separate data category (see warranty-telemetry) and are never
                // sampled, so warranty events are always delivered in full.
                sampleRate: 0.1,
                enableLogs: true,
                environment: window.wp_ajax.WP_ENV,
                release: window.wp_ajax.SENTRY_RELEASE,
                beforeSend(event, hint) {
                    if(isThirdPartyTelemetryEvent(event, hint)) {
                        return null;
                    }

                    return event;
                },
                ignoreErrors: [
                    'top.GLOBALS',
                    'originalCreateNotification',
                    'canvas.contentDocument',
                    'MyApp_RemoveAllHighlights',
                    'http://tt.epicplay.com',
                    'Can\'t find variable: ZiteReader',
                    'jigsaw is not defined',
                    'ComboSearch is not defined',
                    'http://loading.retry.widdit.com/',
                    'atomicFindClose',
                    'fb_xd_fragment',
                    'bmi_SafeAddOnload',
                    'EBCallBackMessageReceived',
                    'conduitPage',
                    'Script error.',
                    'Event `CustomEvent` (type=error) captured as exception',
                    "The play() request was interrupted by",
                    "play() failed because the user",
                    "The fetching process for the media resource was aborted by the user agent at the user's request",
                    "not allowed by the user agent or the platform in the current context, possibly because the user denied permission",
                    "The URL is not available because of the video's privacy settings",
                    "maps/api/js",
                    "ResizeObserver loop completed with undelivered notifications",
                    "roof-designer",
                    "api/player",
                    "Unexpected token '...'. Expected a property name",
                    "player.embed.getVideoUrl"
                ],
                denyUrls: [
                    /graph\.facebook\.com/i,
                    /connect\.facebook\.net\/en_US\/all\.js/i,
                    /eatdifferent\.com\.woopra-ns\.com/i,
                    /static\.woopra\.com\/js\/woopra\.js/i,
                    /extensions\//i,
                    /^chrome:\/\//i,
                    /127\.0\.0\.1:4001\/isrunning/i,
                    /webappstoolbarba\.texthelp\.com\//i,
                    /metrics\.itunes\.apple.com\.edgesuite\.net\//i
                ]
            });
        });
    });
}

function getPageBootTasks() {
    const tasks = [];

    if(document.querySelector('.front-page')) {
        tasks.push(() => import('./pages/front-page').then(({ FrontPage }) => {
            window.frontPage = new FrontPage(document.querySelector('.front-page'));
        }));
    }

    if(document.querySelector('.why-malarkey')) {
        tasks.push(() => import('./pages/why-malarkey').then(({WhyMalarkey}) => {
            window.whyMalarkey = new WhyMalarkey(document.querySelector('.why-malarkey'));
        }));
    }

    if(document.querySelector('.single')) {
        tasks.push(() => import('./pages/single').then(({Single}) => {
            window.single = new Single(document.querySelector('.single'));
        }));
    }

    if(document.querySelector('.archive')) {
        tasks.push(() => import('./pages/archive').then(({Archive}) => {
            window.archive = new Archive(document.querySelector('.archive'));
        }));
    }

    if(document.querySelector('.page-template-default')) {
        tasks.push(() => import('./pages/default').then(({DefaultPageTemplate}) => {
            window.single = new DefaultPageTemplate(document.querySelector('.page-template-default'));
        }));
    }

    if(document.querySelector('.site__content .single-product')) {
        tasks.push(() => import('./pages/single-product').then(({SingleProduct}) => {
            window.singleProduct = new SingleProduct(document.querySelector('.site__content .single-product'));
        }));
    }

    if(document.querySelector('.single-company')) {
        tasks.push(() => import('./pages/single-company').then(({SingleCompany}) => {
            window.singleCompany = new SingleCompany(document.querySelector('.single-company'));
        }));
    }

    if(document.querySelector('.page-gallery')) {
        tasks.push(() => import('./pages/page-gallery').then(({GalleryPage}) => {
            window.single = new GalleryPage(document.querySelector('.page-gallery'));
        }));
    }

    if(document.querySelector('.page-documents-resources')) {
        tasks.push(() => import('./pages/page-documents-resources').then(({DocumentsPage}) => {
            window.single = new DocumentsPage(document.querySelector('.page-documents-resources'));
        }));
    }

    if(document.querySelector('.find-a-page')) {
        tasks.push(() => import('./pages/find-a-page').then(({FindAPage}) => {
            window.findAPage = new FindAPage(document.querySelector('.find-a-page'));
        }));
    }

    if(document.querySelector('.submit-warranties')) {
        tasks.push(() => import('./pages/submit-warranties').then(({SubmitWarranties}) => {
            window.submitWarranties = new SubmitWarranties(document.querySelector('.submit-warranties'));
        }));
    }

    if(document.querySelector('.page-search')) {
        tasks.push(() => import('./pages/page-search').then(({SearchPage}) => {
            window.single = new SearchPage(document.querySelector('.page-search'));
        }));
    }

    if(document.querySelector('.announcement')) {
        tasks.push(() => import('./modules/announcement').then(({Announcement}) => {
            window.announcement = new Announcement(document.querySelector('.announcement'));
        }));
    }

    if(document.querySelector('.contractor-warranties')) {
        tasks.push(() => import('./pages/contractor-warranties').then(({ContractorWarranties}) => {
            window.contractorWarranties = new ContractorWarranties(document.querySelector('.contractor-warranties'));
        }));
    }

    return tasks;
}

installTelemetryFetchGuard();
installTelemetryRejectionGuard();

runWhenDomReady(() => {
    initSkipLink();
    initCriticalNavigation();
    initDeferredNavigation();
    initFormInputs();
    initSentry();

    runWhenIdle(() => {
        runChunkedTasks(getPageBootTasks());
    });
});
